import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';

// --- Types ---

export type WebFormFieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'textarea'
  | 'select'
  | 'checkbox'
  | 'date'
  | 'number'
  | 'hidden';

export interface WebFormField {
  id: string;
  type: WebFormFieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  defaultValue?: string;
  order: number;
}

export type WebFormSubmitAction = 'create_contact' | 'create_lead' | 'create_deal' | 'custom';

export interface WebForm {
  id: string;
  name: string;
  description?: string;
  fields: WebFormField[];
  submitAction: WebFormSubmitAction;
  assigneeId?: string;
  assigneeName?: string;
  redirectUrl?: string;
  successMessage?: string;
  styling?: {
    buttonText?: string;
    buttonColor?: string;
    backgroundColor?: string;
    fontFamily?: string;
  };
  isActive: boolean;
  submissionCount: number;
  lastSubmissionAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WebFormsResponse {
  forms: WebForm[];
  total: number;
}

export interface WebFormAnalytics {
  totalSubmissions: number;
  submissionsThisWeek: number;
  submissionsThisMonth: number;
  conversionRate: number;
  topReferrers: { url: string; count: number }[];
}

// --- Hooks ---

export function useWebForms() {
  return useQuery({
    queryKey: ['web-forms'],
    queryFn: () => fetchJson<WebFormsResponse>('/api/web-forms'),
  });
}

export function useWebForm(id: string | null) {
  return useQuery({
    queryKey: ['web-form', id],
    queryFn: () => fetchJson<WebForm>(`/api/web-forms/${id}`),
    enabled: !!id,
  });
}

export function useCreateWebForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<WebForm>) =>
      postJson<WebForm>('/api/web-forms', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['web-forms'] });
    },
  });
}

export function useUpdateWebForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<WebForm> }) =>
      putJson<WebForm>(`/api/web-forms/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['web-forms'] });
      queryClient.invalidateQueries({ queryKey: ['web-form', variables.id] });
    },
  });
}

export function useDeleteWebForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteJson(`/api/web-forms/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['web-forms'] });
    },
  });
}

export function useFormAnalytics(id: string | null) {
  return useQuery({
    queryKey: ['web-form-analytics', id],
    queryFn: () => fetchJson<WebFormAnalytics>(`/api/web-forms/${id}/analytics`),
    enabled: !!id,
  });
}
